import { ActionIcon, Box, Group, LoadingOverlay, Tooltip } from '@mantine/core';
import { useElementSize } from '@mantine/hooks';
import { IconPlus, IconTablePlus, IconTrash, IconVocabulary } from '@tabler/icons-react';
import type React from 'react';
import type { ComponentProps } from 'react';
import type { OverrideProperties } from 'type-fest';
import { Show } from '../../../../../react-commons/components/control-flow/Show';
import { MantineTable } from '../../../../../react-commons/components/table/MantineTable';
import { EditDeleteButtons } from '../base/EditDeleteButtons';

type CrudValidationTableProps<R extends object> = {
    onAdd: (() => void) | undefined;
    onAddDisabled?: boolean;
    onBatchAdd?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    onBatchDelete?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    onEdit: ((resource: R, index: number) => void) | undefined;
    onDelete: ((resource: R, index: number) => void) | undefined;
    onIndexChange?: (resource: R, oldIndex: number, newIndex: number) => void;
    onOverview?: (resource: R, index: number) => void;
    batchDeleteEnabled?: boolean;
    readonly?: boolean;
} &
    OverrideProperties<
        Omit<ComponentProps<typeof MantineTable<R, true>>, 'enableActions' | 'rowActionsWidth' | 'rowActionsHeader'>,
        { tableOptions?: Omit<ComponentProps<typeof MantineTable<R, true>>['tableOptions'], 'renderRowActions'> }
    >;

export const CrudValidationTable = <R extends object>({
                                                          batchDeleteEnabled,
                                                          onAdd,
                                                          onAddDisabled,
                                                          onBatchAdd,
                                                          onBatchDelete,
                                                          onEdit,
                                                          onDelete,
                                                          onIndexChange,

                                                          onOverview,
                                                          tableOptions,
                                                          readonly,
                                                          ...props
                                                      }: CrudValidationTableProps<R>) => {
    const rowActionsWidth = Math.max([onEdit, onDelete].filter(Boolean).length * 28, 28);
    const rowDraggingEnabled = onIndexChange?.let((it) => Boolean(it));
    const rowDraggingDisabled = rowDraggingEnabled?.let((it) => !it);
    const { ref, width: measuredWidth } = useElementSize();
    const width = Math.max(rowActionsWidth, measuredWidth);
    return (
        <MantineTable<R, true>
            {...props}
            tableOptions={{
                ...tableOptions,
                renderRowActions: ({ row: { original: resource, index } }) => (
                    <Group wrap={'nowrap'} gap={6} w={width} justify={'end'}>
                        <Show when={onOverview}>
                            {(onO) => (
                                <Tooltip label={'Overview'}>
                                    <ActionIcon
                                        variant={'transparent'}
                                        color={'var(--mantine-text-secondary-color)'}
                                        onClick={() => onO(resource, index)}
                                        size={'sm'}
                                    >
                                        <IconVocabulary />
                                    </ActionIcon>
                                </Tooltip>
                            )}
                        </Show>
                        <EditDeleteButtons
                            handleEdit={onEdit ? () => onEdit(resource, index) : undefined}
                            handleDelete={onDelete ? () => onDelete(resource, index) : undefined}
                        />
                    </Group>
                ),
                enableRowOrdering: rowDraggingEnabled,
                autoResetPageIndex: rowDraggingDisabled,
                enableSorting: rowDraggingDisabled,
                mantineRowDragHandleProps:
                    rowDraggingEnabled ?
                        (props) => ({
                            onDragEnd: () => {
                                if (!onIndexChange) {
                                    return;
                                }
                                const { draggingRow, hoveredRow } = props.table.getState();
                                const resource = draggingRow?.original;
                                const originalIndex = draggingRow?.index;
                                const newIndex = hoveredRow?.index;

                                if (resource && originalIndex !== undefined && newIndex !== undefined) {
                                    onIndexChange(resource, originalIndex, newIndex);
                                }
                            },
                        })
                        : undefined,
            }}
            enableActions={true}
            rowActionsWidth={width}
            rowActionsHeader={
                <Group ref={ref} wrap={'nowrap'} gap={6} justify={'end'}>
                    <Show when={onAdd}>
                        <Tooltip label={'Add'}>
                            <ActionIcon
                                variant={'transparent'}
                                color={'var(--mantine-text-secondary-color)'}
                                onClick={onAdd}
                                size={'sm'}
                                disabled={onAddDisabled === true || readonly}
                            >
                                <IconPlus />
                            </ActionIcon>
                        </Tooltip>
                    </Show>
                    <Show when={onBatchAdd}>
                        {(onBA) => (
                            <Tooltip label={'Batch Add'}>
                                <ActionIcon
                                    variant={'transparent'}
                                    color={'var(--mantine-text-secondary-color)'}
                                    onClick={onBA}
                                    size={'sm'}
                                    disabled={readonly}
                                >
                                    <IconTablePlus />
                                </ActionIcon>
                            </Tooltip>
                        )}
                    </Show>
                    <Show when={onBatchDelete}>
                        {(onBD) => (
                            <Tooltip label={'Bulk Delete'}>
                                <ActionIcon
                                    disabled={!batchDeleteEnabled}
                                    variant={'transparent'}
                                    color={'var(--mantine-text-secondary-color)'}
                                    onClick={onBD}
                                    size={'sm'}
                                >
                                    <IconTrash />
                                </ActionIcon>
                            </Tooltip>
                        )}
                    </Show>
                    <Box pos="relative" h={22}>
                        <LoadingOverlay
                            visible={false}
                            loaderProps={{
                                size: 'xs',
                                style: {
                                    position: 'absolute',
                                    top: 2,
                                    left: 2,
                                },
                            }}
                        />
                    </Box>
                </Group>
            }
        />
    );
};
